import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Sparkles, Baby, Heart, Share2, X, Crown } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import { useAuth } from "@/App";

const STORAGE_KEY = "whats_new_seen_v1";

const FeatureRow = ({ icon: Icon, iconClass, title, desc, delay }) => (
  <motion.div
    initial={{ opacity: 0, x: -10 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.35, delay }}
    className="flex items-start gap-4 py-3"
  >
    <div className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${iconClass}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div className="flex-1 min-w-0">
      <h4 className="text-white font-semibold text-[15px] leading-tight">{title}</h4>
      <p className="text-white/65 text-sm mt-1 leading-relaxed">{desc}</p>
    </div>
  </motion.div>
);

export default function WhatsNewModal() {
  const { t } = useTranslation();
  const { subscription } = useAuth();
  const [open, setOpen] = useState(false);
  const isPremium = subscription?.is_premium ?? null;

  useEffect(() => {
    try {
      const seen = localStorage.getItem(STORAGE_KEY);
      if (!seen) {
        const timeout = setTimeout(() => setOpen(true), 1200);
        return () => clearTimeout(timeout);
      }
    } catch {
      /* ignore storage errors */
    }
  }, []);

  const dismiss = (persist = true) => {
    setOpen(false);
    trackEvent(
      persist ? "whats_new_dismissed_persist" : "whats_new_dismissed_later",
      "whats_new"
    );
    if (persist) {
      try {
        localStorage.setItem(STORAGE_KEY, String(Date.now()));
      } catch {
        /* ignore */
      }
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm px-4 py-6"
          onClick={() => dismiss(false)}
          data-testid="whats-new-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg rounded-3xl bg-gradient-to-br from-[#1d1036] via-[#1a0f2e] to-[#0d0821] border border-white/10 shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto"
          >
            <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-pink-500/15 via-purple-500/10 to-transparent pointer-events-none" />

            <button
              type="button"
              onClick={() => dismiss(true)}
              className="absolute top-4 right-4 z-10 text-white/60 hover:text-white transition"
              aria-label="Close"
              data-testid="whats-new-close-btn"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="relative p-7 sm:p-8">
              <div className="flex items-center gap-2 text-pink-300 text-xs uppercase tracking-widest mb-3">
                <Sparkles className="w-4 h-4" />
                {t("whatsNew.title")}
              </div>
              <h3 className="text-2xl sm:text-3xl font-serif text-white leading-tight">
                {t("whatsNew.subtitle")}
              </h3>

              <div className="mt-5 divide-y divide-white/5">
                <FeatureRow
                  icon={Sparkles}
                  iconClass="bg-purple-500/20 text-purple-300"
                  title={t("whatsNew.aiInsightsTitle")}
                  desc={t("whatsNew.aiInsightsDesc")}
                  delay={0.05}
                />
                <FeatureRow
                  icon={Baby}
                  iconClass="bg-pink-500/20 text-pink-300"
                  title={t("whatsNew.pregnancyTitle")}
                  desc={t("whatsNew.pregnancyDesc")}
                  delay={0.12}
                />
                <FeatureRow
                  icon={Heart}
                  iconClass="bg-rose-500/20 text-rose-300"
                  title={t("whatsNew.ttcTitle")}
                  desc={t("whatsNew.ttcDesc")}
                  delay={0.19}
                />
                <FeatureRow
                  icon={Share2}
                  iconClass="bg-emerald-500/20 text-emerald-300"
                  title={t("whatsNew.partnerTitle")}
                  desc={t("whatsNew.partnerDesc")}
                  delay={0.26}
                />
              </div>

              {isPremium === false && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.35 }}
                  className="mt-6 rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-500/15 via-yellow-500/5 to-amber-500/10 p-5"
                  data-testid="whats-new-premium-upsell"
                >
                  <div className="flex items-start gap-3">
                    <div className="shrink-0 w-10 h-10 rounded-xl bg-amber-400/25 flex items-center justify-center">
                      <Crown className="w-5 h-5 text-amber-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-amber-100 font-semibold text-[15px]">
                        {t("whatsNew.premiumTitle")}
                      </h4>
                      <p className="text-amber-100/75 text-sm mt-1 leading-relaxed">
                        {t("whatsNew.premiumDesc")}
                      </p>
                    </div>
                  </div>
                  <Button
                    asChild
                    size="sm"
                    className="mt-4 w-full bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-amber-950 font-semibold rounded-full"
                    data-testid="whats-new-upgrade-btn"
                  >
                    <Link to="/settings?tab=subscription" onClick={() => { trackEvent("upgrade_clicked", "whats_new"); dismiss(true); }}>
                      <Crown className="w-4 h-4 mr-2" />
                      {t("whatsNew.premiumCta")}
                    </Link>
                  </Button>
                </motion.div>
              )}

              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <Button
                  type="button"
                  onClick={() => dismiss(true)}
                  data-testid="whats-new-got-it-btn"
                  className="flex-1 bg-pink-500 hover:bg-pink-600 text-white rounded-full py-5"
                >
                  {t("whatsNew.gotIt")}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => dismiss(false)}
                  data-testid="whats-new-later-btn"
                  className="flex-1 border-white/20 text-white/80 hover:bg-white/5 rounded-full py-5"
                >
                  {t("whatsNew.later")}
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
